<script setup lang="ts">
/**
 * SlotGroup — Renders N slots for a slot type with colored header
 * Maps modules to their slot positions, shows empty slots for unfilled positions
 */
import type { FitModule, FitDetail, SlotType, ModuleState } from '../../../shared/types';
import ModuleSlot from './ModuleSlot.vue';

const props = defineProps<{
  slotType: SlotType;
  totalSlots: number;
  modules: FitModule[];
  fit: FitDetail;
}>();

const emit = defineEmits<{
  'click-empty': [slotType: SlotType, slotPosition: number];
  'set-state': [moduleId: number, state: ModuleState];
  'remove': [moduleId: number];
  'context-menu': [event: MouseEvent, module: FitModule];
  'drop': [slotType: SlotType, slotPosition: number, typeID: number, typeName: string];
}>();

const SLOT_LABELS: Record<string, string> = {
  high: 'High Slots',
  med: 'Mid Slots',
  low: 'Low Slots',
  rig: 'Rig Slots',
  subsystem: 'Subsystems',
  service: 'Service Slots',
};

function getModuleForSlot(pos: number): FitModule | null {
  return props.modules.find(m => m.slotPosition === pos) ?? null;
}
</script>

<template>
  <div class="slot-group" v-if="totalSlots > 0">
    <div class="slot-group-header" :class="`slot-${slotType}`">
      <span class="slot-label">{{ SLOT_LABELS[slotType] || slotType }}</span>
      <span class="slot-count">{{ modules.length }}/{{ totalSlots }}</span>
    </div>

    <div class="slot-list">
      <ModuleSlot
        v-for="pos in totalSlots"
        :key="pos"
        :module="getModuleForSlot(pos - 1)"
        :slot-type="slotType"
        :slot-position="pos - 1"
        @click-empty="(st, sp) => emit('click-empty', st, sp)"
        @set-state="(id, state) => emit('set-state', id, state)"
        @remove="(id) => emit('remove', id)"
        @context-menu="(e, mod) => emit('context-menu', e, mod)"
        @drop="(st, sp, tid, tn) => emit('drop', st, sp, tid, tn)"
      />
    </div>
  </div>
</template>

<style scoped>
.slot-group {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 7px;
  background: rgba(0, 0, 0, 0.12);
  border: 1px solid rgba(30, 42, 61, 0.72);
  border-radius: var(--radius-md);
}

.slot-group-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 8px;
  border-radius: var(--radius-sm);
  font-family: var(--font-mono);
  font-size: 0.66rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  border: 1px solid transparent;
}

.slot-label {
  flex: 1;
}

.slot-count {
  opacity: 0.85;
  font-weight: 700;
  color: var(--text-primary);
}

.slot-high {
  background: linear-gradient(90deg, rgba(229, 57, 53, 0.16), transparent);
  color: var(--high-slot);
  border-color: rgba(229, 57, 53, 0.22);
}

.slot-med {
  background: linear-gradient(90deg, rgba(30, 136, 229, 0.16), transparent);
  color: var(--med-slot);
  border-color: rgba(30, 136, 229, 0.22);
}

.slot-low {
  background: linear-gradient(90deg, rgba(67, 160, 71, 0.16), transparent);
  color: var(--low-slot);
  border-color: rgba(67, 160, 71, 0.22);
}

.slot-rig {
  background: linear-gradient(90deg, rgba(142, 36, 170, 0.16), transparent);
  color: var(--rig-slot);
  border-color: rgba(142, 36, 170, 0.22);
}

.slot-subsystem {
  background: linear-gradient(90deg, rgba(255, 152, 0, 0.16), transparent);
  color: var(--accent-orange);
  border-color: rgba(255, 152, 0, 0.22);
}

.slot-service {
  background: linear-gradient(90deg, rgba(156, 39, 176, 0.16), transparent);
  color: var(--accent-purple);
  border-color: rgba(156, 39, 176, 0.22);
}

.slot-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
</style>
